import React, { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { SectionCategory, Section } from "@/types/models/page";
import { useSectionCategories } from "@/hooks/useSectionCategories";
import { SectionCard } from "./SectionCard";

interface SectionPanelProps {
  categoryId: string;
  onSectionSelect: (section: Section) => void;
}

export function SectionPanel({
  categoryId,
  onSectionSelect,
}: SectionPanelProps) {
  const [category, setCategory] = useState<SectionCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchCategoryById } = useSectionCategories();

  useEffect(() => {
    let isMounted = true;
    
    const loadCategory = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log("🔍 SectionPanel: Fetching category with ID:", categoryId);
        const categoryData = await fetchCategoryById(categoryId);
        console.log("🔍 SectionPanel: Received categoryData:", categoryData);
        console.log("🔍 SectionPanel: categoryData type:", typeof categoryData);
        console.log("🔍 SectionPanel: categoryData keys:", categoryData ? Object.keys(categoryData) : 'null');
        
        if (isMounted) {
          setCategory(categoryData);
        }
      } catch (err) {
        console.error("Error fetching category:", err);
        if (isMounted) {
          setError(
            err instanceof Error ? err.message : "Failed to load category",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (categoryId) {
      loadCategory();
    }
    
    return () => {
      isMounted = false;
    };
  }, [categoryId, fetchCategoryById]);

  const handleSectionSelect = (section: Section) => {
    console.log("Adding section to page:", section);
    onSectionSelect(section);
  };

  const handleSectionDragStart = (section: Section) => (e: React.DragEvent) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        type: "section",
        data: section,
      }),
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading sections...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-destructive mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">
            Failed to load sections
          </p>
          <p className="text-xs text-muted-foreground mt-1">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-xs text-primary hover:text-primary/80 underline mt-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Category not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 overflow-y-auto h-full">
      {category.sections && category.sections.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {category.sections.length} section
              {category.sections.length !== 1 ? "s" : ""} available
            </div>
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: category.color }}
              title={category.name}
            />
          </div>

          {/* Sections Grid - Single column for better preview visibility */}
          <div className="space-y-3">
            {category.sections.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                onClick={() => handleSectionSelect(section)}
                onDragStart={handleSectionDragStart(section)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center mb-3">
            <span className="text-2xl">📦</span>
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            No sections available
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            This category doesn&apos;t have any sections yet
          </p>
        </div>
      )}
    </div>
  );
}
